import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { AddPropertyContext } from "./AddPropertyContext";
import { Dropdown } from "primereact/dropdown";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { ILoan, ILoanPayload, Property, IPropertyPayload } from "../../../interface/tracker";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchMemeberDetail } from "../../../store/slice/analyserSlice";
import { IMemberEntity } from "../../../interface/analysed";
import { httpPost, httpUpdate } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import {
  MEMBER_KEY,
  AU_CAPITAL_CITY,
  PROPERTY_QUALITY,
  PROPERTY_TYPE,
  AU_CONSTRUCTION_YEAR,
  USER_DETAIL,
  NZ_RENOVATIONS,
} from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { REIButton, REIDialoge } from "../../../components/ui";
import EntityOwnerShip from "../../ProppertyAnalyser/PropertyStepper/Child/AddEntityOwnership";
import MyEntityGoalsIndex from "../MyEntityAndGoals";
import { WBLogo, VaultLogo as ValutTLogo } from "./../../../images";
import { Toast } from "primereact/toast";
import { convertToMMDDYYYY, getDateStringWithGivenFormat, isMMDDYYFormat, parseDate } from "../../../utils/commonUtil";
import { useFilterSelection, FilterSelectionProvider } from "../FilterSelectionContext";

interface ITaxAndMarketProps {
  onNext: () => void;
  onBack: () => void;
  portfolioTrackerId?: number;
  isEditMode?: boolean;
}

const TaxAndMarket = ({ onNext, onBack, portfolioTrackerId, isEditMode }: ITaxAndMarketProps) => {
  const context = useContext(AddPropertyContext);
  const [calculateDepreciation, setCalculateDepreciation] = useState(true);
  const dispatch: AppDispatch = useDispatch();
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const { memeberEntity } = useSelector((state: RootState) => state.analyser);
  const [entities, setEntities] = useState<{ label: string; value: number }[]>([]);
  const [errors, setErrors] = useState({
    WBNearestCityId: "",
    WBPropertyTypeId: "",
    EstimatedLandValue: "",
    ConstructionYear: "",
  });
  const [showEntityOwnershipDialog, setShowEntityOwnershipDialog] = useState(false);
  const [showEntityGoalsDialog, setShowEntityGoalsDialog] = useState(false);
  const [selectedEntityIdForView, setSelectedEntityIdForView] = useState<number | null>(null);
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const [isPorfolioSuccess, setIsPorfolioSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setSelectedEntityId } = useFilterSelection();

  useEffect(() => {
    dispatch(fetchMemeberDetail());
  }, [dispatch]);

  useEffect(() => {
    if (context?.userProfile && !context.property.PortfolioTrackerId) {
      handleInputChange("ConsumerPriceIndexRate", context.userProfile.TaxRateMarketFactors.ConsumerPrice);
      handleInputChange("CapitalGrowthRate", context.userProfile.TaxRateMarketFactors.CapitalGrowth);
    }
  }, [context?.userProfile]);

  if (!context) {
    return <div>Loading...</div>;
  }

  const { property, setProperty } = context;

  useEffect(() => {
    if (memeberEntity) {
      const entityOptions = memeberEntity.map((e: IMemberEntity) => ({
        label: e.EntityName,
        value: e.MemberEntityId,
      }));
      setEntities(entityOptions);
      const currentEntityExists = entityOptions.some((e) => e.value === context?.property.MemberEntityId);
      context?.setProperty({
        ...context?.property,
        MemberEntityId:
          context?.property.MemberEntityId && currentEntityExists
            ? context.property.MemberEntityId
            : entityOptions[0]?.value,
      });
    }
  }, [memeberEntity]);

  useEffect(() => {
    if (context?.property) {
      if (isEditMode && Number(context?.property.WBPropertyTypeId || 0) == 0) {
        handleInputChange("WBPropertyTypeId", 1);
      }
      const medianBody = {
        BathroomId: context?.property?.BathroomCode?.toString(),
        BedroomId: context?.property?.BedroomCode?.toString(),
        CarportId: context?.property?.ParkingCode?.toString(),
        CountryId: context?.property?.CountryCode?.toString(),
        DateAdded: context?.property?.PurchaseDate,
        Landsize: context?.property?.LandSize_sqm?.toString(),
        MemberPropertyId: Number(context?.property?.MemberPropertyId),
        ModulesId: context?.property?.MemberId,
        PropertyDetailId: 0,
        PropertyName: context?.property?.Name || "",
        PropertyTypeId: Number(context?.property?.PropertyTypeId).toString(),
        UserCountryId: memberDetail.Country == "AU" ? 1 : 0,
        district: context?.property?.District || "",
        locality: context?.property?.Suburb,
        postcode: context?.property?.PostCode,
        state: context?.property?.StateRegion,
        useDefault: "0",
      };
      if (medianBody) {
        httpPost(`${ApiEndPoints.analyser}/GetMedians`, medianBody)
          .then((response) => {
            if (response.status === 200 && response.data) {
              const median = response.data as any;
              if (!isEditMode) {
                handleInputChange("WBQualityTypeCode", median.WBQualityTypeId);
                handleInputChange("WBPropertyTypeId", parseInt(median.WBPropertyTypeId));
                handleInputChange("WBNearestCityId", parseInt(median.WBNearestCity));
                handleInputChange("ConstructionYear", parseInt(median.ConstructionCompleteId));
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching median records:", error);
          });
      }
    }
  }, []);

  const handleInputChange = (field: keyof Property, value: any) => {
    if (setProperty) {
      setProperty((prev: Partial<Property>) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const validate = () => {
    const newErrors = { WBNearestCityId: "", WBPropertyTypeId: "", EstimatedLandValue: "", ConstructionYear: "" };
    let isValid = true;

    if (calculateDepreciation && !property.WBNearestCityId && context.property?.CountryCode !== 2) {
      newErrors.WBNearestCityId = "Nearest capital city is required.";
      isValid = false;
    }

    if (
      (!property.WBPropertyTypeId || Number(property.WBPropertyTypeId || 0) === 0) &&
      context.property?.CountryCode !== 2
    ) {
      newErrors.WBPropertyTypeId = "Property type is required.";
      isValid = false;
    }

    if (context.property?.CountryCode?.toString() === "2" && Number(property.EstimatedLandValue) <= 0) {
      newErrors.EstimatedLandValue = "Estimated land value is required for New Zealand properties.";
      isValid = false;
    }

    if (
      context.property?.CountryCode?.toString() === "2" &&
      (!property.ConstructionYear ||
        Number(property.ConstructionYear) <= 0 ||
        property.ConstructionYear.toString() === "")
    ) {
      newErrors.ConstructionYear = "Construction year is required for New Zealand properties.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const getDepositAmount = () => {
    let depositAmount = 0;
    if (isEditMode == undefined || isEditMode == false) {
      depositAmount =
        Number(property.DepositCash) <= 100
          ? Number(property.PurchasePrice) * (Number(property.DepositCash) / 100)
          : (Number(property.DepositCash) ?? 0);
    } else {
      depositAmount = Number(property.DepositCash) ?? 0;
    }
    return depositAmount;
  };

  const getPostPayload = (): IPropertyPayload => {
    if (!property) return {} as IPropertyPayload;

    const isExistingLoan = !portfolioTrackerId && property.PurchaseType === "existing";

    let isNewLoan: boolean;
    if (property.PurchaseType === "new") {
      isNewLoan = true;
    } else if (property.PurchaseType === "existing") {
      isNewLoan = property.IsExistingLoan == undefined || property.IsExistingLoan ? false : true;
    }

    const loans: ILoanPayload[] =
      property.Loans?.filter((loan) => loan.TrackerLoanId === 0).map((loan) => {
        const loanCopy: any = { ...loan };
        [
          "isNameModified",
          "uid",
          "isReviewDateManuallySet",
          "isDisplayExtraFields",
          "ModifiedFields",
          "_reviewDateManuallySet",
          "isProRatedExpenseDateManuallySet",
        ].forEach((k) => {
          if (Object.prototype.hasOwnProperty.call(loanCopy, k)) {
            delete loanCopy[k];
          }
        });

        const rest = loanCopy;

        const convertedLoan: any = {};
        Object.entries(rest).forEach(([key, value]) => {
          if (key === "TrackerLoanId") {
            convertedLoan[key] = value;
          } else {
            convertedLoan[key] = value != null ? value.toString() : "";
          }
        });
        let dateValue = loan.Date;
        let reviewDateValue = loan.InterestRateReviewDate;
        if (typeof dateValue === "string" && !isMMDDYYFormat(dateValue)) {
          dateValue = convertToMMDDYYYY(dateValue);
        }
        if (typeof reviewDateValue === "string" && !isMMDDYYFormat(reviewDateValue)) {
          reviewDateValue = convertToMMDDYYYY(reviewDateValue);
        }

        convertedLoan.Date = getDateStringWithGivenFormat(new Date(dateValue), "dd/mm/yy");
        loan.InterestRateReviewDate
          ? (convertedLoan.InterestRateReviewDate = getDateStringWithGivenFormat(new Date(reviewDateValue), "dd/mm/yy"))
          : (convertedLoan.InterestRateReviewDate = "");
        convertedLoan.EstablishmentFees = isNewLoan ? Number(loan.EstablishmentFees) || 0 : 0;
        convertedLoan.ValuationFees = isNewLoan ? Number(loan.ValuationFees) || 0 : 0;
        convertedLoan.OtherLoanCost = isNewLoan ? Number(loan.OtherLoanCost) || 0 : 0;
        convertedLoan.OtherLoanCosts = isNewLoan ? Number(loan.OtherLoanCosts) || 0 : 0;
        convertedLoan.Length_Months = Number(loan.Length_Months).toString();
        convertedLoan.Length_Years = Number(loan.Length_Years).toString();
        convertedLoan.FixedInterestPeriod_Months = Number(loan.FixedInterestPeriod_Months).toString();
        convertedLoan.FixedInterestPeriod_Years = Number(loan.FixedInterestPeriod_Years).toString();
        convertedLoan.isNewLoan = Number(isNewLoan ? 1 : 0);
        return convertedLoan;
      }) || [];

    const existingLoans: ILoan[] =
      property.ExistingLoans?.filter((loan) => loan.TrackerLoanId !== 0).map((loan) => {
        loan.isNewLoan = 0;
        const { isNameModified, uid, isReviewDateManuallySet, isDisplayExtraFields, ...rest } = loan;
        const convertedLoan: any = {};
        Object.entries(rest).forEach(([key, value]) => {
          if (key === "TrackerLoanId" || key === "isNewLoan") {
            convertedLoan[key] = value;
          } else {
            convertedLoan[key] = value != null ? value.toString() : "";
          }
        });

        let dateValue = loan.Date;
        let reviewDateValue = loan.InterestRateReviewDate;

        if (typeof dateValue === "string" && !isMMDDYYFormat(dateValue)) {
          dateValue = convertToMMDDYYYY(dateValue);
        }
        if (typeof reviewDateValue === "string" && !isMMDDYYFormat(reviewDateValue)) {
          reviewDateValue = convertToMMDDYYYY(reviewDateValue);
        }

        convertedLoan.InterestRateReviewDate = reviewDateValue
          ? getDateStringWithGivenFormat(new Date(reviewDateValue), "dd/mm/yy")
          : "";
        convertedLoan.Date = dateValue ? getDateStringWithGivenFormat(new Date(dateValue), "dd/mm/yy") : "";

        return convertedLoan;
      }) || [];

    return {
      MemberId: localStorage.getItem(MEMBER_KEY) || "",
      MemberEntityId: property.MemberEntityId || 0,
      PortionOwned: property.PortionOwned || 0,
      userCountryCode: memberDetail.Country || "AU",
      CountryCode: Number(property.CountryCode || 0),
      StateRegion: property.StateRegion || "",
      District: property.District || null,
      Suburb: property.Suburb || "",
      PostCode: property.PostCode || "",
      StreetAddress: property.StreetAddress || "",
      Name: property.Name || "",
      PropertyTypeId: property.PropertyTypeId || 0,
      BedroomCode: Number(property.BedroomCode || 0),
      BathroomCode: Number(property.BathroomCode || 0),
      ParkingCode: Number(property.ParkingCode || 0),
      LandSize_sqm: property.LandSize_sqm || 0,
      FloorSize_sqm: property.LandSize_sqm || 0,
      PurchasePrice: !isExistingLoan ? property.PurchasePrice || 0 : property.Current_InitialPurchasePrice || 0,
      DepositCash: getDepositAmount(),
      PurchaseDate: property.PurchaseDate || getDateStringWithGivenFormat(new Date(Date.now()), "dd/mm/yy"),
      EstimatedMarketValue: !isExistingLoan ? property.EstimatedMarketValue || 0 : property.Current_MarketValue || 0,
      EstimatedMarketValueDate: property.EstimatedMarketValueDate || "",
      EstimatedLandValue: property.EstimatedLandValue || 0,
      EstimatedLandValueDate: property.EstimatedLandValueDate || "",
      Rental_IsResidential: property.Rental_IsResidential?.toString() || "",
      Rental_NumWeeks: property.Rental_NumWeeks || 0,
      Rental_OccupancyRate: property.Rental_OccupancyRate || 0,
      Rental_WeeklyRent: property.Rental_WeeklyRent || 0,
      Rental_Peak_NumWeeks: property.Rental_Peak_NumWeeks || 0,
      Rental_Peak_OccupancyRate: property.Rental_Peak_OccupancyRate || 0,
      Rental_Peak_WeeklyRent: property.Rental_Peak_WeeklyRent || 0,
      Rental_Shoulder_NumWeeks: property.Rental_Shoulder_NumWeeks || 0,
      Rental_Shoulder_OccupancyRate: property.Rental_Shoulder_OccupancyRate || 0,
      Rental_Shoulder_WeeklyRent: property.Rental_Shoulder_WeeklyRent || 0,
      Rental_OffPeak_NumWeeks: property.Rental_OffPeak_NumWeeks || 0,
      Rental_OffPeak_OccupancyRate: property.Rental_OffPeak_OccupancyRate || 0,
      Rental_OffPeak_WeeklyRent: property.Rental_OffPeak_WeeklyRent || 0,
      Expense_Rates: parseInt(property.Expense_Rates?.toString() || "0", 10),
      Expense_Insurance: parseInt(property.Expense_Insurance?.toString() || "0", 10),
      Expense_LandTax: parseInt(property.Expense_LandTax?.toString() || "0", 10),
      Expense_LandLease: parseInt(property.Expense_LandLease?.toString() || "0", 10),
      Expense_RepairsMaintenance: parseInt(property.Expense_RepairsMaintenance?.toString() || "0", 10),
      Expense_ServiceContracts: parseInt(property.Expense_ServiceContracts?.toString() || "0", 10),
      Expense_GardeningFees: parseInt(property.Expense_GardeningFees?.toString() || "0", 10),
      Expense_LettingFees: parseInt(property.Expense_LettingFees?.toString() || "0", 10),
      Expense_BodyCorporateFees: parseInt(property.Expense_BodyCorporateFees?.toString() || "0", 10),
      Expense_CleaningFees: parseInt(property.Expense_CleaningFees?.toString() || "0", 10),
      Expense_OtherExpenses: parseInt(property.Expense_OtherExpenses?.toString() || "0", 10),
      Expense_AgentsRentalCommissionRate: Number(property.Expense_AgentsRentalCommissionRate?.toString() || "0"),
      Lease_Tenants: property.Lease_Tenants || "",
      Lease_StartDate: property.Lease_StartDate || "",
      Lease_EndDate: property.Lease_EndDate || "",
      Lease_ReviewDate: property.Lease_ReviewDate || "",
      Lease_ReviewAlert: property.Lease_ReviewAlert?.toString() || "",
      Year1ProRata: property.Year1ProRata?.toString() || "",
      Year1ProRataDateExpense: property.Year1ProRata?.toString() == "1" ? property.Year1ProRataDateExpense || "" : "",
      Year1ProRataDateIncome: property.Year1ProRata?.toString() == "1" ? property.Year1ProRataDateIncome || "" : "",
      ConsumerPriceIndexRate: property.ConsumerPriceIndexRate || 0,
      CapitalGrowthRate: property.CapitalGrowthRate || 0,
      LoanToValueRatioTarget: property.LoanToValueRatioTarget || 0,
      Cost_CapitalImprovements: property.Cost_CapitalImprovements || 0,
      Cost_SolicitorFees: property.Cost_SolicitorFees || 0,
      Cost_StampDuty: parseInt(property.Cost_StampDuty?.toString() || "0"),
      Cost_OtherCosts: property.Cost_OtherCosts || 0,

      Depreciation_ApplyFrom: property.Depreciation_ApplyFrom || "",
      ConstructionYear: property.ConstructionYear || 0,
      CalculateDepreciation: property.CalculateDepreciation || "",
      WBNearestCityId: property.WBNearestCityId || 0,
      WBPropertyTypeId: Number(property.WBPropertyTypeId || 0),
      WBQualityTypeCode: Number(property.WBQualityTypeCode || 0),
      VIRenovationTypeCode: Number(property.VIRenovationTypeCode || 0),
      Loans: loans ?? [],
      ...(portfolioTrackerId
        ? {
            PortfolioTrackerId: portfolioTrackerId,
            ExistingLoans: existingLoans ?? [],
          }
        : undefined),
    };
  };

  const saveProperty = async () => {
    if (!property) return;

    const payload = getPostPayload();

    setTimeout(async () => {
      try {
        setLoading(true);
        let result;
        if (portfolioTrackerId) {
          result = await httpUpdate(`${ApiEndPoints.tracker}PortfolioTracker/Tracker/`, payload);
        } else {
          result = await httpPost(`${ApiEndPoints.tracker}PortfolioTracker/Add/`, payload, null);
        }

        if (result.status == 200 && result.data) {
          setLoading(false);
          if (!portfolioTrackerId) {
            localStorage.setItem("portfolioTrackerId", result.data);
          }
          setIsPorfolioSuccess(true);
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: "Error Message",
            detail: "Failed to save property.",
            life: 3000,
          });
          setLoading(false);
        }
      } catch (error) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error Message",
          detail: "Failed to save property.",
          life: 3000,
        });
        setLoading(false);
      }
    }, 100);
  };

  const handleSaveAndReview = async () => {
    if (validate()) {
      await saveProperty();
    }
  };

  const hideEntityOwnershipDialog = async (result?: any) => {
    await dispatch(fetchMemeberDetail());
    setTimeout(() => {
      if (result && result.data) {
        handleInputChange("MemberEntityId", result.data);
      }
    }, 1000);
    setShowEntityOwnershipDialog(false);
  };

  return (
    <div className="p-fluid">
      <REIDialoge
        showHeader={true}
        header={`Add a new entity ownership structure`}
        visible={showEntityOwnershipDialog}
        handleEditClick={hideEntityOwnershipDialog}
        position="top"
        customClass="update-statistics-dialog"
        style={{ width: "55vw" }}
        contentComponent={<EntityOwnerShip closeDialogue={hideEntityOwnershipDialog} />}
      ></REIDialoge>
      <REIDialoge
        showHeader={true}
        header="My Entity & Goals"
        visible={showEntityGoalsDialog}
        handleEditClick={async () => {
          setShowEntityGoalsDialog(false);
          setSelectedEntityIdForView(null);
          try {
            await dispatch(fetchMemeberDetail());
          } catch (e) {
            console.error("Failed to fetch member detail:", e);
          }
        }}
        position="top"
        style={{ width: "90vw", maxHeight: "90vh" }}
        customClass="entity-goals-dialog"
        contentComponent={
          <FilterSelectionProvider initialEntityId={selectedEntityIdForView ?? 0}>
            <div style={{ height: "75vh", overflow: "auto" }}>
              <MyEntityGoalsIndex />
            </div>
          </FilterSelectionProvider>
        }
      ></REIDialoge>
      <REIDialoge
        contentComponent={
          <>
            {!portfolioTrackerId && (
              <h3>The property has been successfully added to your portfolio tracker portfolio</h3>
            )}
            {portfolioTrackerId && <h3>Your changes have been saved</h3>}
            <div>
              {property.StreetAddress}, {property?.StateRegion}, {property?.PostCode}{" "}
              {Number(property.CountryCode) === 1 ? "(AU)" : "(NZ)"}
            </div>
            <div className="flex row mt-1">
              <REIButton
                id={"aptm-btn-ok"}
                label="OK"
                disabled={false}
                icon=""
                classNames=""
                onClick={() => {
                  onNext();
                }}
              />
            </div>
          </>
        }
        handleEditClick={() => {
          onNext();
          setIsPorfolioSuccess(false);
        }}
        header="Property added to Portfolio Tracker"
        showHeader={false}
        visible={isPorfolioSuccess}
      ></REIDialoge>
      <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Assign an entity</h3>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap mb-2">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/2 text-left" htmlFor="Rates">
                  This property belongs to entity:
                </label>
                <Dropdown
                  id="aptm-drop-entity"
                  data-testid="aptm-drop-entity"
                  options={entities}
                  value={property.MemberEntityId}
                  onChange={(e) => handleInputChange("MemberEntityId", e.value)}
                  placeholder="Select"
                  className="w-full"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex">
                <Button
                  id="aptm-btn-createentity"
                  data-testid="aptm-btn-createentity"
                  label="Create a new entity"
                  className="p-button-link p-0 pt-2 text-wrap w-52"
                  onClick={() => {
                    setShowEntityOwnershipDialog(true);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap mb-1">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="entityName">
                  Entity selected :
                </label>
                <label className="w-2/3 text-left" htmlFor="PortionOwned">
                  Entity Owns
                </label>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 p-2 pt-0 justify-evenly">
            <div className="flex flex-row items-center align-middle">
              <div className="w-1/3 flex items-center" id="entityName">
                {entities && (
                  <>
                    <label className="text-left">
                      <b>{entities.find((e) => e.value === property.MemberEntityId)?.label}</b>
                    </label>
                    <Button
                      id="aptm-btn-viewentity"
                      data-testid="aptm-btn-viewentity"
                      icon="pi pi-user-edit"
                      label="View"
                      className="p-button-link ml-2 w-32"
                      onClick={() => {
                        setSelectedEntityIdForView(property.MemberEntityId ?? null);
                        setShowEntityGoalsDialog(true);
                        if (property.MemberEntityId) {
                          setSelectedEntityId(property.MemberEntityId);
                        }
                      }}
                    />
                  </>
                )}
              </div>
              <div className="w-2/3 flex flex-row content-center items-center">
                <Slider
                  id="aptm-sldr-portionowned"
                  data-testid="aptm-sldr-portionowned"
                  value={property.PortionOwned}
                  onChange={(e: any) => handleInputChange("PortionOwned", e.value)}
                  className="w-full mr-3"
                  min={1}
                  max={100}
                  step={1}
                />
                <InputNumber
                  id="aptm-num-portionowned"
                  data-testid="aptm-num-portionowned"
                  value={property.PortionOwned}
                  onValueChange={(e) => handleInputChange("PortionOwned", e.value!)}
                  useGrouping={true}
                  min={1}
                  max={100}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Market factors</h3>
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div className="flex items-center">
            <label htmlFor="cpi" className="w-1/3">
              Consumer price Index (%) *
            </label>
            <div className="w-2/3 flex items-center gap-2">
              <Slider
                id="cpi"
                data-testid="aptm-sldr-cpi"
                value={(property.ConsumerPriceIndexRate || 0) >= 10 ? 10 : property.ConsumerPriceIndexRate || 0}
                onChange={(e: any) => handleInputChange("ConsumerPriceIndexRate", e.value)}
                className="flex-grow"
                min={0}
                max={10}
                step={0.1}
              />
              <InputNumber
                id="aptm-num-cpi"
                data-testid="aptm-num-cpi"
                value={property.ConsumerPriceIndexRate}
                onValueChange={(e) => handleInputChange("ConsumerPriceIndexRate", e.value!)}
                className="w-1/3"
                mode="decimal"
                max={100}
                minFractionDigits={2}
                maxFractionDigits={2}
              />
            </div>
          </div>
          <div className="flex items-center">
            <label htmlFor="capitalGrowth" className="w-1/3">
              Capital growth (%) *
            </label>
            <div className="w-2/3 flex items-center gap-2">
              <Slider
                id="capitalGrowth"
                data-testid="aptm-sldr-capitalgrowth"
                value={(property.CapitalGrowthRate || 0) >= 10 ? 10 : property.CapitalGrowthRate || 0}
                onChange={(e: any) => handleInputChange("CapitalGrowthRate", e.value)}
                className="flex-grow"
                min={0}
                max={10}
                step={0.1}
              />
              <InputNumber
                id="aptm-num-capitalgrowth"
                data-testid="aptm-num-capitalgrowth"
                value={property.CapitalGrowthRate}
                onValueChange={(e) => handleInputChange("CapitalGrowthRate", e.value!)}
                className="w-1/3"
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
              />
            </div>
          </div>
          <div className={"flex items-center " + (context.property?.CountryCode?.toString() === "2" ? "hidden" : "")}>
            <label htmlFor="landTax" className="w-1/3">
              Land tax ($)
            </label>
            <div className="w-2/3 flex items-center gap-2">
              <Slider
                id="landTax"
                data-testid="aptm-sldr-landtax"
                value={(property.Expense_LandTax || 0) >= 20000 ? 20000 : property.Expense_LandTax || 0}
                onChange={(e: any) => handleInputChange("Expense_LandTax", e.value)}
                className="flex-grow"
                min={0}
                max={20000}
              />
              <InputNumber
                id="aptm-num-landtax"
                data-testid="aptm-num-landtax"
                value={property.Expense_LandTax}
                onValueChange={(e) => handleInputChange("Expense_LandTax", e.value!)}
                className="w-1/3"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Depreciation estimation</h3>
        </div>
        <div className="flex justify-between">
          <div className="w-full text-left md:w-1/2 p-2">
            <div className="flex items-center">
              {context.property?.CountryCode == 1 && (
                <a
                  target="_blank"
                  href="http://www.realestateinvestar.com.au/depreciationreport/"
                  id="aptm-lnk-washingtonbrown"
                  data-testid="aptm-lnk-washingtonbrown"
                >
                  <em className="float-left">Powered by</em>
                  <img className="float-left ml-4 mr-4 w-20" src={WBLogo}></img>
                  <div className="float-left">
                    <strong>Washington Brown</strong>
                    <br />
                    Order depreciation report
                  </div>
                </a>
              )}
              {context.property?.CountryCode == 2 && (
                <a
                  target="_blank"
                  href="http://www.realestateinvestar.co.nz/depreciation/"
                  id="aptm-lnk-valult"
                  data-testid="aptm-lnk-valult"
                >
                  <em className="float-left">Powered by</em>
                  <img className="float-left ml-4 mr-4 w-20" src={ValutTLogo}></img>
                  <div className="float-left">
                    <strong>ValulT</strong>
                    <br />
                    Specialists in property depreciation
                  </div>
                </a>
              )}
              <Checkbox
                inputId="calcDepreciation"
                data-testid="aptm-chk-calcdepreciation"
                onChange={(e) => setCalculateDepreciation(e.checked || false)}
                checked={calculateDepreciation}
                className="ml-10"
              ></Checkbox>
              <label htmlFor="calcDepreciation" className="ml-2">
                Calculate depreciation
              </label>
            </div>
          </div>
          <div className="flex items-center"></div>
        </div>
        {/* Hide this section for New Zealand */}
        {context.property?.CountryCode?.toString() === "1" && (
          <div className={"grid grid-cols-2 gap-4 mt-4"}>
            <div className="flex flex-col">
              <label>Quality</label>
              <Dropdown
                id="aptm-drop-quality"
                data-testid="aptm-drop-quality"
                options={PROPERTY_QUALITY}
                value={Number(property.WBQualityTypeCode)}
                optionValue="value"
                optionLabel="level"
                onChange={(e) => handleInputChange("WBQualityTypeCode", e.value)}
                placeholder="Select Quality"
              />
            </div>
            <div className="flex flex-col">
              <label>Property type</label>
              <Dropdown
                id="aptm-drop-propertytype"
                data-testid="aptm-drop-propertytype"
                options={PROPERTY_TYPE}
                value={Number(property.WBPropertyTypeId)}
                optionValue="value"
                optionLabel="type"
                onChange={(e) => handleInputChange("WBPropertyTypeId", e.value)}
                placeholder="Select Property Type"
              />
              {errors.WBPropertyTypeId && <small className="p-error">{errors.WBPropertyTypeId}</small>}
            </div>
            <div className="flex flex-col">
              <label>Nearest capital city</label>
              <Dropdown
                id="aptm-drop-nearestcity"
                data-testid="aptm-drop-nearestcity"
                options={AU_CAPITAL_CITY}
                value={Number(property.WBNearestCityId)}
                onChange={(e) => handleInputChange("WBNearestCityId", e.value)}
                placeholder="Select"
                optionValue="value"
                optionLabel="city"
                className={errors.WBNearestCityId ? "p-invalid" : ""}
              />
              {errors.WBNearestCityId && <small className="p-error">{errors.WBNearestCityId}</small>}
            </div>
            <div className="flex flex-col">
              <label>Construction year complete</label>
              <Dropdown
                id="aptm-drop-constructionyear"
                data-testid="aptm-drop-constructionyear"
                options={AU_CONSTRUCTION_YEAR}
                value={property.ConstructionYear}
                onChange={(e) => handleInputChange("ConstructionYear", e.value)}
                placeholder="Select Year"
                optionValue="value"
                optionLabel="year"
              />
            </div>
          </div>
        )}

        {/* Hide this section for Australia */}
        {context.property?.CountryCode?.toString() === "2" && (
          <>
            <div className="grid grid-cols-2 gap-8">
              <div className="flex items-center">
                <label htmlFor="constructionYear" className="w-1/3">
                  Construction year complete
                </label>
                <div className="w-2/3">
                  <Dropdown
                    id="constructionYear"
                    data-testid="aptm-drop-constructionyearnz"
                    options={AU_CONSTRUCTION_YEAR}
                    value={property.ConstructionYear}
                    onChange={(e) => handleInputChange("ConstructionYear", e.value)}
                    placeholder="Select Year"
                    optionValue="value"
                    optionLabel="year"
                    className={errors.ConstructionYear ? "p-invalid" : ""}
                  />
                  {errors.ConstructionYear && <small className="p-error">{errors.ConstructionYear}</small>}
                </div>
              </div>
              <div className="flex items-center">
                <label htmlFor="recentRenovations" className="w-1/3">
                  Recent renovations
                </label>
                <div className="w-2/3">
                  <Dropdown
                    id="recentRenovations"
                    data-testid="aptm-drop-recentrenovations"
                    options={NZ_RENOVATIONS}
                    value={Number(property.VIRenovationTypeCode)}
                    onChange={(e) => handleInputChange("VIRenovationTypeCode", e.value)}
                    placeholder="Select renovation type"
                    optionValue="value"
                    optionLabel="renovationType"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8 mt-2">
              <div className="flex items-center">
                <label htmlFor="landValue" className="w-1/3">
                  Your estimate of land value ($)
                </label>
                <div className="w-2/3 flex items-center gap-2">
                  <Slider
                    id="landValue"
                    data-testid="aptm-sldr-landvalue"
                    value={(property.EstimatedLandValue || 0) >= 20000 ? 20000 : property.EstimatedLandValue || 0}
                    onChange={(e: any) => handleInputChange("EstimatedLandValue", e.value)}
                    className={errors.EstimatedLandValue ? "p-invalid flex-grow" : "flex-grow"}
                    min={0}
                    max={20000}
                  />
                  <InputNumber
                    id="aptm-num-landvalue"
                    data-testid="aptm-num-landvalue"
                    value={property.EstimatedLandValue}
                    onValueChange={(e) => handleInputChange("EstimatedLandValue", e.value!)}
                    className={errors.EstimatedLandValue ? "p-invalid w-1/3" : "w-1/3"}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8 mt-2">
              <div className="flex items-center">
                <div className="w-1/3"></div>
                <div className="w-2/3 flex items-center gap-2">
                  {errors.EstimatedLandValue && <small className="p-error">{errors.EstimatedLandValue}</small>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between mt-4">
        <div>
          <Button
            id="aptm-btn-back"
            data-testid="aptm-btn-back"
            label="Rent & expenses"
            icon="pi pi-arrow-left"
            onClick={onBack}
            className="p-button-secondary"
          />
        </div>
        <div>
          <Button
            id="aptm-btn-saveandreview"
            data-testid="aptm-btn-saveandreview"
            label="Save and review"
            icon="pi pi-arrow-right"
            iconPos="right"
            loading={loading}
            onClick={handleSaveAndReview}
          />
        </div>
      </div>
    </div>
  );
};

export default TaxAndMarket;
